/**
 * VISUALIZATION AGENTS CONFIG
 *
 * Add agent names here (must match the manifest name exactly, case-sensitive)
 * to enable the "Create Diagram" and "Export to Excel" buttons in the chat header.
 *
 * Each entry also defines the predefined prompt that is sent silently to the
 * agent to request data in the VisualizationData JSON format.
 */

export interface VisualizationAgentConfig {
  /** Agent name as it appears in the manifest (case-sensitive). */
  agentName: string;
  /** Prompt sent to the agent to request structured visualization data. */
  dataPrompt: string;
  /**
   * Optional prompt used specifically for Excel exports.
   * When provided, the agent is asked to return raw tabular rows so that
   * every column appears in the exported spreadsheet.
   * Falls back to `dataPrompt` when not set.
   */
  excelPrompt?: string;
}

/** JSON schema description embedded in the prompt so the agent knows what to return. */
const JSON_FORMAT_DESCRIPTION = `{
  "title": "string — chart title",
  "traces": [
    {
      "name": "string — series name",
      "type": "bar | line | scatter | pie",
      "x": ["string or number values for bar/line/scatter x-axis"],
      "y": [0, 0, 0],
      "labels": ["string values for pie chart labels"],
      "values": [0, 0, 0]
    }
  ],
  "layout": {
    "xaxis": { "title": "string" },
    "yaxis": { "title": "string" }
  }
}`;

/** JSON schema for the Excel-oriented raw-data prompt. */
const EXCEL_FORMAT_DESCRIPTION = `{
  "title": "string — sheet title",
  "traces": [],
  "rawData": [
    {
      "Column1": "value",
      "Column2": 123
    }
  ]
}`;

export const VISUALIZATION_AGENT_CONFIGS: VisualizationAgentConfig[] = [
  {
    agentName: 'EMPTY',
    dataPrompt:
      `Please put the data from the last message in the following JSON format. DO NOT CALL THE AGENT AGAIN ` +
      `Return ONLY the raw JSON object with no surrounding text, markdown, or code fences:\n\n` +
      JSON_FORMAT_DESCRIPTION,
    excelPrompt:
      `Please put the data from the last message in the following JSON format. DO NOT CALL THE AGENT AGAIN` +
      `Include ALL original columns and rows exactly as they are — do not aggregate, ` +
      `pivot, or drop any columns. Return ONLY the raw JSON object with no surrounding ` +
      `text, markdown, or code fences:\n\n` +
      EXCEL_FORMAT_DESCRIPTION,
  }
];

const VISUALIZATION_AGENT_NAMES = new Set(
  VISUALIZATION_AGENT_CONFIGS.map((c) => c.agentName),
);

export const isVisualizationAgent = (agentName: string | undefined): boolean =>
  agentName != null && VISUALIZATION_AGENT_NAMES.has(agentName);

export const getVisualizationConfig = (
  agentName: string,
): VisualizationAgentConfig | undefined =>
  VISUALIZATION_AGENT_CONFIGS.find((c) => c.agentName === agentName);
